/**
 * Formular-Assistent Komponente
 * - Analysiert gescannte Formulare
 * - Zeigt automatische Befüllung aus Klientenakten
 * - Verwaltet Rückfragen und fehlende Daten
 */

import { useState, useEffect } from 'react';
import {
  FileSearch, CheckCircle, XCircle, AlertTriangle, HelpCircle,
  FileText, Paperclip, Download, ChevronDown, ChevronUp,
  RefreshCw, ArrowRight, Upload, Eye, Edit2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
// Dialog imports removed - all forms are now inline
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_ICONS = {
  gefuellt: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  leer: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  unsicher: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
  manuell_erforderlich: { icon: Edit2, color: 'text-blue-600', bg: 'bg-blue-50' },
};

export default function FormularAssistent({ 
  dokumentId = null, 
  ocrText = null, 
  klientId = null,
  onClose = null 
}) {
  const [loading, setLoading] = useState(false);
  const [analyse, setAnalyse] = useState(null);
  const [formularTypen, setFormularTypen] = useState([]);
  const [selectedTyp, setSelectedTyp] = useState('');
  const [editingFelder, setEditingFelder] = useState({});
  const [showRueckfragen, setShowRueckfragen] = useState(true);

  useEffect(() => {
    loadFormularTypen();
  }, []);

  const loadFormularTypen = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/formular/typen`);
      const data = await res.json();
      setFormularTypen(data);
    } catch (e) {
      console.error(e);
    }
  };

  const analysiereFormular = async (typOverride = null) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/formular/analysieren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dokument_id: dokumentId,
          ocr_text: ocrText,
          klient_id: klientId || null,
          formular_typ: typOverride || selectedTyp || null
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Analyse fehlgeschlagen');
        return;
      }
      
      setAnalyse(data);
      
      if (data.erkannt) {
        toast.success(`${data.formular_name} erkannt`);
      } else {
        toast.info('Formulartyp nicht erkannt - bitte manuell auswählen');
      }
    } catch (e) {
      console.error(e);
      toast.error('Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleFeldEdit = (feldName, wert) => {
    setEditingFelder(prev => ({ ...prev, [feldName]: wert }));
  };

  const getProgress = () => {
    if (!analyse?.zusammenfassung) return 0;
    const { gefuellt = 0, ausgefuellt = 0, gesamt = 0 } = analyse.zusammenfassung;
    const filled = gefuellt || ausgefuellt;
    return gesamt > 0 ? Math.round((filled / gesamt) * 100) : 0;
  };

  // Kein OCR-Text vorhanden
  if (!ocrText && !dokumentId) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileSearch className="w-14 h-14 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 font-medium">Kein Formular zum Analysieren</p>
          <p className="text-sm text-slate-400 mt-2">
            Bitte scannen Sie zuerst ein Formular im Posteingang oder wählen Sie ein Dokument aus.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="formular-assistent">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <FileSearch className="w-5 h-5" />
            ZEO Formular-Assistent
          </h3>
          <p className="text-sm text-slate-500">
            Automatische Erkennung und Befüllung von Antragsformularen
          </p>
        </div>
        {!analyse && (
          <div className="flex items-center gap-2">
            <Select value={selectedTyp || 'auto'} onValueChange={v => setSelectedTyp(v === 'auto' ? '' : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Formulartyp (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatisch erkennen</SelectItem>
                {formularTypen.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => analysiereFormular()} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
              Analysieren
            </Button>
          </div>
        )}
      </div>

      {/* Analyse nicht erkannt - Typ auswählen */}
      {analyse && !analyse.erkannt && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Formulartyp nicht erkannt</p>
                <p className="text-sm text-amber-700 mt-1">{analyse.meldung}</p>
                
                {analyse.erkannte_felder?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-amber-700">Erkannte Felder: {analyse.erkannte_felder.map(f => f.name).join(', ')}</p>
                  </div>
                )}
                
                <div className="flex items-center gap-2 mt-3">
                  <Select value={selectedTyp} onValueChange={setSelectedTyp}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Formulartyp auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {analyse.verfuegbare_typen?.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => analysiereFormular(selectedTyp)} disabled={!selectedTyp || loading}>
                    Erneut analysieren
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analyse erfolgreich */}
      {analyse?.erkannt && (
        <>
          {/* Übersicht */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{analyse.formular_name}</CardTitle>
                  <CardDescription>
                    Für: {analyse.klient?.name}
                    {analyse.formular_herausgeber && <span className="ml-2 text-slate-400">| {analyse.formular_herausgeber}</span>}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {analyse.ki_analysiert && (
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                      KI-analysiert
                    </Badge>
                  )}
                  {analyse.lern_status === 'gelernt' && (
                    <Badge className="bg-green-100 text-green-700 border-green-300">
                      Gelernt
                    </Badge>
                  )}
                  {analyse.lern_status === 'standard' && (
                    <Badge variant="outline" className="text-blue-700 border-blue-300">
                      Standard
                    </Badge>
                  )}
                  {analyse.lern_status && analyse.lern_status.startsWith('bekannt') && (
                    <Badge className="bg-teal-100 text-teal-700 border-teal-300">
                      {analyse.lern_status}
                    </Badge>
                  )}
                  {analyse.erkennungs_score > 0 && (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      Score: {analyse.erkennungs_score}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Fortschritt */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Automatisch befüllt</span>
                  <span className="font-medium">{analyse.zusammenfassung.gefuellt} / {analyse.zusammenfassung.gesamt} Felder</span>
                </div>
                <Progress value={getProgress()} className="h-2" />
              </div>
              
              {/* Status-Übersicht */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">{analyse.zusammenfassung.gefuellt} Gefüllt</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">{analyse.zusammenfassung.fehlend} Fehlend</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700">{analyse.zusammenfassung.unsicher} Unsicher</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50">
                  <Edit2 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700">{analyse.zusammenfassung.manuell} Manuell</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ATHENA Nachricht */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="py-4">
              <div className="prose prose-sm max-w-none text-slate-700">
                <div dangerouslySetInnerHTML={{ 
                  __html: analyse.athena_nachricht
                    .replace(/## /g, '<h4 class="text-base font-semibold mt-4 mb-2">')
                    .replace(/### /g, '<h5 class="text-sm font-semibold mt-3 mb-1">')
                    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n- /g, '<br>• ')
                    .replace(/\n/g, '<br>')
                }} />
              </div>
            </CardContent>
          </Card>

          {/* Felder-Liste */}
          <Accordion type="single" collapsible defaultValue="felder">
            <AccordionItem value="felder">
              <AccordionTrigger className="text-base font-medium">
                Formularfelder ({analyse.felder?.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {analyse.felder?.map((feld, idx) => {
                    const statusInfo = STATUS_ICONS[feld.status] || STATUS_ICONS.leer;
                    const StatusIcon = statusInfo.icon;
                    const isEditing = editingFelder[feld.name] !== undefined;
                    
                    return (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${statusInfo.bg} border-slate-200`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 flex-1">
                            <StatusIcon className={`w-5 h-5 mt-0.5 ${statusInfo.color}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{feld.label}</span>
                                {feld.pflicht && <Badge variant="outline" className="text-xs">Pflicht</Badge>}
                              </div>
                              
                              {isEditing ? (
                                <Input 
                                  value={editingFelder[feld.name]} 
                                  onChange={e => handleFeldEdit(feld.name, e.target.value)}
                                  className="mt-2"
                                />
                              ) : (
                                <>
                                  {feld.wert ? (
                                    <p className="text-sm text-slate-700 mt-1">{feld.wert}</p>
                                  ) : (
                                    <p className="text-sm text-slate-400 italic mt-1">Kein Wert</p>
                                  )}
                                </>
                              )}
                              
                              {feld.quelle && (
                                <p className="text-xs text-slate-500 mt-1">Quelle: {feld.quelle}</p>
                              )}
                              {feld.hinweis && (
                                <p className="text-xs text-amber-600 mt-1">⚠️ {feld.hinweis}</p>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              if (isEditing) {
                                // Speichern
                                setEditingFelder(prev => {
                                  const copy = { ...prev };
                                  delete copy[feld.name];
                                  return copy;
                                });
                              } else {
                                handleFeldEdit(feld.name, feld.wert || '');
                              }
                            }}
                          >
                            {isEditing ? <CheckCircle className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Anlagen */}
            <AccordionItem value="anlagen">
              <AccordionTrigger className="text-base font-medium">
                Empfohlene Anlagen ({analyse.anlagen?.length})
                {analyse.fehlende_anlagen > 0 && (
                  <Badge variant="destructive" className="ml-2">{analyse.fehlende_anlagen} fehlen</Badge>
                )}
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {analyse.anlagen?.map((anlage, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${anlage.gefunden ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-start gap-2">
                        {anlage.gefunden ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{anlage.beschreibung}</p>
                          
                          {anlage.gefunden && anlage.dokumente?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {anlage.dokumente.map((dok, didx) => (
                                <div key={didx} className="flex items-center gap-2 text-sm text-green-700">
                                  <Paperclip className="w-3 h-3" />
                                  <span>{dok.titel}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {anlage.hinweis && (
                            <p className="text-sm text-red-600 mt-1">{anlage.hinweis}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Rückfragen */}
            {analyse.rueckfragen?.length > 0 && (
              <AccordionItem value="rueckfragen">
                <AccordionTrigger className="text-base font-medium">
                  Rückfragen ({analyse.rueckfragen.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {analyse.rueckfragen.map((frage, idx) => (
                      <Card key={idx} className="border-amber-200">
                        <CardContent className="py-3">
                          <div className="flex items-start gap-2">
                            <HelpCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-slate-800">{frage.frage}</p>
                              {frage.vorschlaege?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-500 mb-1">Vorschläge zur Beschaffung:</p>
                                  <ul className="text-sm text-slate-600 space-y-1">
                                    {frage.vorschlaege.map((v, vidx) => (
                                      <li key={vidx} className="flex items-center gap-1">
                                        <ArrowRight className="w-3 h-3" />
                                        {v}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Hinweise */}
            {analyse.hinweise?.length > 0 && (
              <AccordionItem value="hinweise">
                <AccordionTrigger className="text-base font-medium">
                  Wichtige Hinweise ({analyse.hinweise.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2">
                    {analyse.hinweise.map((hinweis, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                        <span className="text-blue-600">💡</span>
                        <p className="text-sm text-blue-800">{hinweis}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Aktionen */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="outline" onClick={() => setAnalyse(null)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Neue Analyse
            </Button>
            <div className="flex gap-2">
              <Button variant="outline">
                <Eye className="w-4 h-4 mr-2" />
                Vorschau
              </Button>
              <Button>
                <Download className="w-4 h-4 mr-2" />
                PDF erstellen
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
